import { useState, useEffect, useMemo } from 'react';
import { collection, query, onSnapshot, doc, setDoc, updateDoc, runTransaction } from 'firebase/firestore';
import { db } from 'lib/firebase';
import { calculateChoreState, calculateChoreUpdate } from './choreUtils';

export const useChoreLogic = (userId) => {
    const [dbChores, setDbChores] = useState([]);
    const [fixedStatus, setFixedStatus] = useState({});
    const [loading, setLoading] = useState(true);
    const [optimisticStatus, setOptimisticStatus] = useState({});
    const [optimisticOrder, setOptimisticOrder] = useState({}); // { todo: [id, ...], ... }

    // 1. Fetch DB Chores ... (No change)
    useEffect(() => {
        const q = query(collection(db, "chores"));
        const unsubscribe = onSnapshot(q, (snapshot) => {
            const items = [];
            snapshot.forEach((doc) => {
                items.push({ id: doc.id, ...doc.data() });
            });
            setDbChores(items);
        });
        return () => unsubscribe();
    }, []);

    // 2. Fetch Fixed Chore Status & Order
    useEffect(() => {
        if (!userId) return;
        const ref = doc(db, 'fixed_chore_status', userId);
        const unsubscribe = onSnapshot(ref, (docSnap) => {
            if (docSnap.exists()) {
                setFixedStatus(docSnap.data());
            } else {
                setFixedStatus({});
            }
            setLoading(false);
        });
        return () => unsubscribe();
    }, [userId]);

    // 3. Process Logic
    const { kanbanBoard, qualificationStatus: calcQualificationStatus, quotaStats: calcQuotaStats } = useMemo(() => {
        return calculateChoreState(
            userId, 
            dbChores, 
            fixedStatus, 
            { status: optimisticStatus, order: optimisticOrder }
        );
    }, [userId, dbChores, fixedStatus, optimisticStatus, optimisticOrder]);

    const qualificationStatus = calcQualificationStatus;
    const quotaStats = calcQuotaStats;

    // 4. Move Task (Drag & Drop Handler) - Replaces updateTaskStatus
    const moveTask = async (taskId, sourceColId, destColId, newDestIndex) => {
        if (!userId) return;

        // 1. Calculate new Order Arrays
        // We need the CURRENT state of order to manipulate it.
        // We can use the calculated `kanbanBoard` which is already sorted.
        const sourceList = kanbanBoard[sourceColId].map(t => t.id);
        const destList = sourceColId === destColId ? sourceList : kanbanBoard[destColId].map(t => t.id);

        let restoredDate = null; // Track if we restored an old date

        if (sourceColId === destColId) {
             // Reorder within same column
             const currentIndex = sourceList.indexOf(taskId);
             if (currentIndex === -1) return; // Should not happen

             // Remove from current
             sourceList.splice(currentIndex, 1);
             // Insert at new
             sourceList.splice(newDestIndex, 0, taskId);
             
             // Update Optimistic Order ONLY
             setOptimisticOrder(prev => ({
                 ...prev,
                 [sourceColId]: sourceList
             }));
             
             // Persist Order
             const ref = doc(db, 'fixed_chore_status', userId);
             try {
                await setDoc(ref, {}, { merge: true });
                await updateDoc(ref, {
                    [`order.${sourceColId}`]: sourceList
                });
             } catch (e) {
             }

        } else {
            // Move between columns
            const currentSourceIndex = sourceList.indexOf(taskId);
             if (currentSourceIndex > -1) sourceList.splice(currentSourceIndex, 1);
             
             destList.splice(newDestIndex, 0, taskId);
             
             // Update Optimistic State
             setOptimisticStatus(prev => ({ ...prev, [taskId]: destColId }));
             setOptimisticOrder(prev => ({
                 ...prev,
                 [sourceColId]: sourceList,
                 [destColId]: destList
             }));
             
             // Persist Status (Logic from old updateTaskStatus)
            const task = [...kanbanBoard.todo, ...kanbanBoard.doing, ...kanbanBoard.done].find(t => t.id === taskId);
            
             // Persist Order first (or parallel) - Keeping this as simple update for now, or could move into transaction if needed.
             // For drag performance, fire-and-forget order is usually fine.
             // Persist Order first (or parallel) - Keeping this as simple update for now, or could move into transaction if needed.
             // For drag performance, fire-and-forget order is usually fine.
             const ref = doc(db, 'fixed_chore_status', userId);
             await setDoc(ref, {}, { merge: true });
             await updateDoc(ref, {
                 [`order.${sourceColId}`]: sourceList,
                 [`order.${destColId}`]: destList
             });
             
             // Persist Status with TRANSACTION
             if (task) {
                  if (task.type.startsWith('fixed')) {
                      await runTransaction(db, async (transaction) => {
                          const freshDoc = await transaction.get(ref);
                          if (!freshDoc.exists()) return; 
                          const data = freshDoc.data();
                          
                          const { updates, restoredDate: rDate } = calculateChoreUpdate({
                              taskId, destColId, userId, task, dbData: data
                          });
                          
                          restoredDate = rDate; // lift to outer scope
                          transaction.update(ref, updates);
                      });

                  } else if (task.type === 'db') {
                      const choreRef = doc(db, 'chores', task.id);
                      
                      await runTransaction(db, async (transaction) => {
                          const freshSnap = await transaction.get(choreRef);
                          if (!freshSnap.exists()) return;
                          const choreData = freshSnap.data();
                          
                          const { updates, restoredDate: rDate } = calculateChoreUpdate({
                                taskId, destColId, userId, task, dbData: choreData
                          });

                          restoredDate = rDate; // lift to outer scope
                          transaction.update(choreRef, updates);
                      });
                  }
             }
        }
        
        return { success: true, restoredDate };
    };

    return {
        kanbanBoard,
        moveTask,
        qualificationStatus,
        quotaStats,
        loading
    };
};
